/**
 * [data-webgl-hover] — reveal por noise no hover de cards.
 *
 * Anexa um canvas por cima da <img> do card; no hover, anima uProgresso
 * 1→0 (a versão "visor" corrói por cima da foto) e volta ao sair. O RAF só
 * roda durante a transição — custo zero em idle. Sem WebGL2: hover fica com
 * a transição CSS padrão do tema.
 */

import { engine, compilarPrograma, criarContextoLocal } from '@theme/webgl-engine';
import { VERTICE_FULLSCREEN, FRAG_HOVER_REVEAL, comNoise } from '@theme/webgl-shaders';

const VELOCIDADE = 2.6; // progresso por segundo

class HoverReveal {
  constructor(container, img) {
    this.container = container;
    this.img = img;
    this.progresso = 1; // 1 = foto original; 0 = versão visor
    this.alvo = 1;
    this.rafId = 0;
    this.ultimoTs = 0;

    container.addEventListener('pointerenter', () => this.animarPara(0));
    container.addEventListener('pointerleave', () => this.animarPara(1));
  }

  garantirGl() {
    if (this.gl) return true;
    const canvas = document.createElement('canvas');
    canvas.className = 'webgl-hover__canvas';
    canvas.setAttribute('aria-hidden', 'true');
    const gl = criarContextoLocal(canvas);
    if (!gl) return false;

    const programa = compilarPrograma(gl, VERTICE_FULLSCREEN, comNoise(FRAG_HOVER_REVEAL));
    gl.useProgram(programa);
    gl.bindVertexArray(gl.createVertexArray());
    this.uniforms = {
      progresso: gl.getUniformLocation(programa, 'uProgresso'),
      semente: gl.getUniformLocation(programa, 'uSemente'),
    };
    gl.uniform1i(gl.getUniformLocation(programa, 'uFoto'), 0);
    gl.uniform1f(this.uniforms.semente, Math.random() * 40);

    const textura = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, textura);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, this.img);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    canvas.addEventListener('webglcontextlost', (evento) => {
      evento.preventDefault();
      canvas.remove();
      this.gl = null;
    });

    const caixa = this.img.getBoundingClientRect();
    const dpr = Math.min(devicePixelRatio || 1, engine.lod.dprMax);
    canvas.width = Math.max(1, Math.round(caixa.width * dpr));
    canvas.height = Math.max(1, Math.round(caixa.height * dpr));
    gl.viewport(0, 0, canvas.width, canvas.height);

    this.canvas = canvas;
    this.gl = gl;
    this.img.parentElement.appendChild(canvas);
    return true;
  }

  animarPara(alvo) {
    if (!this.img.complete || !this.garantirGl()) return;
    this.alvo = alvo;
    this.canvas.style.opacity = '1';
    if (!this.rafId) {
      this.ultimoTs = performance.now();
      this.rafId = requestAnimationFrame((ts) => this.frame(ts));
    }
  }

  frame(ts) {
    const dt = Math.min((ts - this.ultimoTs) / 1000, 0.05);
    this.ultimoTs = ts;
    const direcao = Math.sign(this.alvo - this.progresso);
    this.progresso += direcao * VELOCIDADE * dt;
    this.progresso = Math.min(1, Math.max(0, this.progresso));

    const gl = this.gl;
    if (!gl || gl.isContextLost()) {
      this.rafId = 0;
      return;
    }
    gl.uniform1f(this.uniforms.progresso, this.progresso);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    if (this.progresso !== this.alvo) {
      this.rafId = requestAnimationFrame((proximoTs) => this.frame(proximoTs));
    } else {
      this.rafId = 0;
      // Em repouso na foto original, esconde o canvas para devolver a <img>
      // (mantém lazy-loading/responsive images como fonte da verdade).
      if (this.progresso === 1) this.canvas.style.opacity = '0';
    }
  }
}

function ativarHoverReveal(raiz = document) {
  if (!engine.lod || !matchMedia('(hover: hover)').matches) return;
  for (const container of raiz.querySelectorAll('[data-webgl-hover]')) {
    if (container.dataset.webglHoverAtivo) continue;
    const img = container.querySelector('img');
    if (!img) continue;
    container.dataset.webglHoverAtivo = 'true';
    new HoverReveal(container, img);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ativarHoverReveal());
} else {
  ativarHoverReveal();
}

export { ativarHoverReveal };
